import type { QueryClient } from '@tanstack/react-query'
import { redirect } from '@tanstack/react-router'
import { FLOW_ROUTES, FLOWS, type Flow } from '@/lib/flows'
import type { SessionUser } from '@/types/auth'
import { fetchMe } from './api'
import { AUTH_KEYS } from './hooks'

// Route-guard helpers for beforeLoad. The router context carries the
// QueryClient (see __root.tsx) so guards resolve /auth/me the same way
// _authenticated.tsx does.

interface GuardContext {
  context: { queryClient: QueryClient }
}

function isAdmin(me: SessionUser): boolean {
  return me.role === 'admin' || me.role === 'fp_admin'
}

// Landing route after login or when a guard bounces the user: the dashboard
// for admins, otherwise the first flow they can access (roles always grant at
// least one flow; the security page is the unreachable-in-practice fallback).
export function landingRouteFor(me: SessionUser): string {
  if (isAdmin(me)) return '/'
  for (const f of FLOWS) {
    if (me.flows.includes(f.key)) return FLOW_ROUTES[f.key]
  }
  return '/settings/security'
}

async function meFor({ context }: GuardContext): Promise<SessionUser> {
  return context.queryClient.ensureQueryData({ queryKey: AUTH_KEYS.me, queryFn: fetchMe })
}

// requireFlow gates a route subtree to users whose role includes the flow
// (admins/fp-admins have every flow).
export function requireFlow(flow: Flow) {
  return async (ctx: GuardContext) => {
    const me = await meFor(ctx)
    if (!me.flows.includes(flow)) throw redirect({ to: landingRouteFor(me) })
  }
}

// requireAdmin gates admin-only pages (the dashboard, user/role management).
export async function requireAdmin(ctx: GuardContext) {
  const me = await meFor(ctx)
  if (!isAdmin(me)) throw redirect({ to: landingRouteFor(me) })
}
